import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber, formatIndianNumber } from '../utils/formatters';

const { width } = Dimensions.get('window');

const SalaryCalculatorScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  
  // State variables
  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);
  const [showBonusDropdown, setShowBonusDropdown] = useState(false);

  // Screen dimensions for responsive design
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isWeb = Platform.OS === 'web';
  const isMobile = screenData.width < 900;

  // Update screen dimensions on resize
  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);
  
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [isCalculated, setIsCalculated] = useState(false);
  
  const [formData, setFormData] = useState({
    ctc: '600000', // Cost to Company
    bonusIncluded: 'percentage', // 'percentage' or 'fixed'
    bonusPercentage: '10',
    monthlyDeductions: false, // Toggle for monthly deductions
    professionalTax: '200',
    employerPF: '2000',
    employeePF: '0',
    employeeInsurance: '0',
    additionalDeduction1: '0',
    additionalDeduction2: '0',
  });

  const bonusOptions = [
    { value: 'percentage', label: 'Percentage' },
    { value: 'fixed', label: 'Fixed Amount' },
  ];

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Parse comma-formatted currency value
  const parseCurrency = (value) => {
    if (!value) return 0;
    return parseFloat(value.toString().replace(/[₹,\s]/g, '')) || 0;
  };

  // Format input with commas
  const formatInput = (value) => {
    if (!value) return '';
    const numericValue = value.replace(/[^\d.]/g, '');
    if (numericValue === '') return '';
    return formatIndianNumber(parseFloat(numericValue));
  };

  // Calculate salary breakdown in real-time
  useEffect(() => {
    if (formData.ctc && !isNaN(parseFloat(formData.ctc))) {
      calculateSalary();
    } else {
      setResults(null);
    }
  }, [formData]);

  const calculateSalary = () => {
    if (!formData.ctc || isNaN(formData.ctc)) {
      setResults(null);
      return;
    }

    const ctc = parseFloat(formData.ctc);
    const bonusPercentage = parseFloat(formData.bonusPercentage) || 0;
    const professionalTax = parseFloat(formData.professionalTax) || 0;
    const employerPF = parseFloat(formData.employerPF) || 0;
    const employeePF = parseFloat(formData.employeePF) || 0;
    const employeeInsurance = parseFloat(formData.employeeInsurance) || 0;
    const additionalDeduction1 = parseFloat(formData.additionalDeduction1) || 0;
    const additionalDeduction2 = parseFloat(formData.additionalDeduction2) || 0;

    // Calculate bonus
    let bonusAmount = 0;
    if (formData.bonusIncluded === 'percentage') {
      bonusAmount = (ctc * bonusPercentage) / 100;
    } else {
      bonusAmount = bonusPercentage; // Fixed amount
    }

    // Calculate basic components
    const grossSalary = ctc - employerPF; // CTC minus employer contributions
    const basicSalary = grossSalary * 0.4; // Typically 40% of gross
    const hra = basicSalary * 0.5; // Typically 50% of basic
    const specialAllowance = grossSalary - basicSalary - hra - bonusAmount;

    // Calculate monthly deductions
    const monthlyProfessionalTax = professionalTax;
    const monthlyEmployeePF = employeePF || (basicSalary * 0.12) / 12; // 12% of basic if not specified
    const monthlyEmployeeInsurance = employeeInsurance;
    const monthlyAdditionalDeduction1 = additionalDeduction1;
    const monthlyAdditionalDeduction2 = additionalDeduction2;

    const totalMonthlyDeductions = monthlyProfessionalTax + monthlyEmployeePF + 
                                   monthlyEmployeeInsurance + monthlyAdditionalDeduction1 + 
                                   monthlyAdditionalDeduction2;

    const totalAnnualDeductions = totalMonthlyDeductions * 12;

    // Calculate net salary
    const netTakeHomeMonthly = (grossSalary / 12) - totalMonthlyDeductions;
    const netTakeHomeAnnual = grossSalary - totalAnnualDeductions;

    // Salary breakdown for table
    const salaryBreakdown = [
      {
        particulars: 'Performance Bonus',
        monthly: bonusAmount / 12,
        yearly: bonusAmount,
      },
      {
        particulars: 'Very Good Pay',
        monthly: specialAllowance / 12,
        yearly: specialAllowance,
      },
      {
        particulars: 'Basic Salary',
        monthly: basicSalary / 12,
        yearly: basicSalary,
      },
      {
        particulars: 'HRA',
        monthly: hra / 12,
        yearly: hra,
      },
    ];

    setResults({
      ctc,
      grossSalary,
      basicSalary,
      hra,
      specialAllowance,
      bonusAmount,
      totalMonthlyDeductions,
      totalAnnualDeductions,
      netTakeHomeMonthly,
      netTakeHomeAnnual,
      salaryBreakdown,
      monthlyProfessionalTax,
      monthlyEmployeePF,
      monthlyEmployeeInsurance,
      monthlyAdditionalDeduction1,
      monthlyAdditionalDeduction2,
    });
    setIsCalculated(true);
  };

  const renderHeader = () => (
    <View style={styles.header}>
      {isMobile && (
        <TouchableOpacity 
          style={styles.hamburgerButton}
          onPress={() => setSidebarVisible(!sidebarVisible)}
        >
          <Ionicons name="menu" size={1} color={colors.text} />
        </TouchableOpacity>
      )}
      <Text style={styles.headerTitle}>Salary Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate your take-home salary, CTC breakdown, and monthly deductions with our comprehensive salary calculator. 
        Get detailed breakdown of your salary components including basic pay, HRA, allowances, and all deductions.
      </Text>
      
      <View style={styles.headerTags}>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>CTC Breakdown</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>Net Salary</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>Deductions</Text>
        </View>
        <View style={styles.headerTag}>
          <Text style={styles.headerTagText}>100% Free</Text>
        </View>
      </View>
    </View>
  );

  const renderCTCInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>Cost to Company (CTC)</Text>
      <View style={styles.inputContainer}>
        <Text style={styles.currencySymbol}>₹</Text>
        <TextInput
          style={styles.input}
          value={formData.ctc}
          onChangeText={(value) => handleInputChange('ctc', value)}
          placeholder="600000"
          placeholderTextColor={colors.textSecondary}
          keyboardType="numeric"
        />
      </View>
    </View>
  );

  const renderBonusInput = () => (
    <View style={styles.inputGroup}>
      <Text style={styles.label}>Bonus Included in CTC as</Text>
      <TouchableOpacity
        style={styles.dropdownContainer}
        onPress={() => setShowBonusDropdown(!showBonusDropdown)}
      >
        <Text style={styles.dropdownText}>
          {bonusOptions.find(option => option.value === formData.bonusIncluded)?.label}
        </Text>
        <Ionicons 
          name={showBonusDropdown ? "chevron-up" : "chevron-down"} 
          size={20} 
          color={colors.textSecondary} 
        />
      </TouchableOpacity>
      
      {showBonusDropdown && (
        <View style={styles.dropdownMenu}>
          {bonusOptions.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={styles.dropdownItem}
              onPress={() => {
                handleInputChange('bonusIncluded', option.value);
                setShowBonusDropdown(false);
              }}
            >
              <Text style={styles.dropdownItemText}>{option.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
      
      <View style={styles.inputContainer}>
        <TextInput
          style={styles.input}
          value={formData.bonusPercentage}
          onChangeText={(value) => handleInputChange('bonusPercentage', value)}
          placeholder={formData.bonusIncluded === 'percentage' ? '10' : '50000'}
          placeholderTextColor={colors.textSecondary}
          keyboardType="numeric"
        />
        <Text style={styles.percentSymbol}>
          {formData.bonusIncluded === 'percentage' ? '%' : '₹'}
        </Text>
      </View>
    </View>
  );

  const renderDeductionsToggle = () => (
    <View style={styles.inputGroup}>
      <View style={styles.toggleRow}>
        <Text style={styles.label}>Monthly Deductions</Text>
        <TouchableOpacity
          style={[styles.checkbox, formData.monthlyDeductions && styles.checkboxActive]}
          onPress={() => handleInputChange('monthlyDeductions', !formData.monthlyDeductions)}
        >
          {formData.monthlyDeductions && (
            <Ionicons name="checkmark" size={16} color="#ffffff" />
          )}
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderDeductionInputs = () => {
    if (!formData.monthlyDeductions) return null;

    return (
      <View style={styles.deductionsContainer}>
        <View style={styles.inputGroup}>
          <Text style={styles.label}>Professional Tax</Text>
          <View style={styles.inputContainer}>
            <Text style={styles.currencySymbol}>₹</Text>
            <TextInput
              style={styles.input}
              value={formData.professionalTax}
              onChangeText={(value) => handleInputChange('professionalTax', value)}
              placeholder="200"
              placeholderTextColor={colors.textSecondary}
              keyboardType="numeric"
            />
          </View>
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.label}>Employer PF</Text>
          <View style={styles.inputContainer}>
            <Text style={styles.currencySymbol}>₹</Text>
            <TextInput
              style={styles.input}
              value={formData.employerPF}
              onChangeText={(value) => handleInputChange('employerPF', value)}
              placeholder="2000"
              placeholderTextColor={colors.textSecondary}
              keyboardType="numeric"
            />
          </View>
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.label}>Employee PF</Text>
          <View style={styles.inputContainer}>
            <Text style={styles.currencySymbol}>₹</Text>
            <TextInput
              style={styles.input}
              value={formData.employeePF}
              onChangeText={(value) => handleInputChange('employeePF', value)}
              placeholder="0"
              placeholderTextColor={colors.textSecondary}
              keyboardType="numeric"
            />
          </View>
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.label}>Employee Insurance</Text>
          <View style={styles.inputContainer}>
            <Text style={styles.currencySymbol}>₹</Text>
            <TextInput
              style={styles.input}
              value={formData.employeeInsurance}
              onChangeText={(value) => handleInputChange('employeeInsurance', value)}
              placeholder="0"
              placeholderTextColor={colors.textSecondary}
              keyboardType="numeric"
            />
          </View>
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.label}>Additional Deduction 1</Text>
          <View style={styles.inputContainer}>
            <Text style={styles.currencySymbol}>₹</Text>
            <TextInput
              style={styles.input}
              value={formData.additionalDeduction1}
              onChangeText={(value) => handleInputChange('additionalDeduction1', value)}
              placeholder="0"
              placeholderTextColor={colors.textSecondary}
              keyboardType="numeric"
            />
          </View>
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.label}>Additional Deduction 2</Text>
          <View style={styles.inputContainer}>
            <Text style={styles.currencySymbol}>₹</Text>
            <TextInput
              style={styles.input}
              value={formData.additionalDeduction2}
              onChangeText={(value) => handleInputChange('additionalDeduction2', value)}
              placeholder="0"
              placeholderTextColor={colors.textSecondary}
              keyboardType="numeric"
            />
          </View>
        </View>
      </View>
    );
  };

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <View style={styles.calculatorRow}>
        <View style={styles.formContent}>
          {renderCTCInput()}
          {renderBonusInput()}
          {renderDeductionsToggle()}
          {renderDeductionInputs()}
        </View>
        
        {!isMobile && (
          <View style={styles.summaryPanel}>
            <View style={styles.panelHeader}>
              <Text style={styles.panelTitle}>Salary Summary</Text>
              <Text style={styles.panelSubtitle}>Net Take Home</Text>
              <Text style={styles.panelAmount}>
                ₹{formatIndianNumber(Math.round(results?.netTakeHomeMonthly || 0))}
              </Text>
            </View>
            
            <View style={styles.taxInfo}>
              <Text style={styles.infoTitle}>Annual CTC</Text>
              <Text style={styles.infoValue}>
                ₹{formatIndianNumber(parseCurrency(formData.ctc))}
              </Text>
            </View>
            
            <View style={styles.taxInfo}>
              <Text style={styles.infoTitle}>Monthly Deductions</Text>
              <Text style={styles.infoValue}>₹{formatIndianNumber(Math.round(results?.totalMonthlyDeductions || 0))}</Text>
            </View>
            
            <View style={styles.taxInfo}>
              <Text style={styles.infoTitle}>Annual Deductions</Text>
              <Text style={styles.infoValue}>₹{formatIndianNumber(Math.round(results?.totalAnnualDeductions || 0))}</Text>
            </View>
            
            <View style={styles.regimeInfo}>
              <Text style={styles.regimeTitle}>
                Your net annual take-home salary will be ₹{formatIndianNumber(Math.round(results?.netTakeHomeAnnual || 0))} 
                after all deductions.
              </Text>
            </View>
          </View>
        )}
      </View>
    </View>
  );

  const renderSalaryBreakdown = () => {
    if (!results) return null;

    return (
      <View style={styles.breakdownContainer}>
        <Text style={styles.breakdownTitle}>Salary Breakdown</Text>
        
        <View style={styles.breakdownTable}>
          <View style={styles.tableHeader}>
            <Text style={styles.tableHeaderText}>Particulars</Text>
            <Text style={styles.tableHeaderText}>Monthly</Text>
            <Text style={styles.tableHeaderText}>Yearly</Text>
          </View>
          
          {results.salaryBreakdown.map((item, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.tableCell}>{item.particulars}</Text>
              <Text style={styles.tableCell}>₹{formatIndianNumber(Math.round(item.monthly))}</Text>
              <Text style={styles.tableCell}>₹{formatIndianNumber(Math.round(item.yearly))}</Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    return (
      <View style={styles.resultsContainer}>
        <View style={styles.resultCard}>
          <Text style={styles.resultTitle}>Total Monthly Deductions</Text>
          <Text style={styles.resultAmount}>₹{formatIndianNumber(Math.round(results.totalMonthlyDeductions))}</Text>
        </View>

        <View style={styles.resultCard}>
          <Text style={styles.resultTitle}>Total Annual Deductions</Text>
          <Text style={styles.resultAmount}>₹{formatIndianNumber(Math.round(results.totalAnnualDeductions))}</Text>
        </View>

        <View style={[styles.resultCard, styles.primaryCard]}>
          <Text style={styles.resultTitle}>Net take home monthly</Text>
          <Text style={[styles.resultAmount, styles.primaryAmount]}>₹{formatIndianNumber(Math.round(results.netTakeHomeMonthly))}</Text>
        </View>

        <View style={[styles.resultCard, styles.primaryCard]}>
          <Text style={styles.resultTitle}>Net take home annual</Text>
          <Text style={[styles.resultAmount, styles.primaryAmount]}>₹{formatIndianNumber(Math.round(results.netTakeHomeAnnual))}</Text>
        </View>
      </View>
    );
  };

  const renderSalaryInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>Understanding Salary Components</Text>
      <Text style={styles.infoText}>
        A salary calculator helps you understand the breakdown of your Cost to Company (CTC) and calculate your actual take-home salary after all deductions. It's essential for financial planning and understanding your true earnings.
      </Text>

      <Text style={styles.infoSubtitle}>Key Salary Components</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>Basic Salary:</Text> The fixed component, typically 40-50% of CTC</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>HRA (House Rent Allowance):</Text> Usually 40-50% of basic salary</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>Special Allowances:</Text> Additional allowances like transport, medical, etc.</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>Bonus:</Text> Performance-based or fixed bonus component</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>Employer PF:</Text> Employer's contribution to Provident Fund</Text>
      </View>

      <Text style={styles.infoSubtitle}>Common Deductions</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>Professional Tax:</Text> State-specific tax (₹200-₹2500 annually)</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>Employee PF:</Text> Your contribution to Provident Fund (12% of basic)</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>Employee Insurance:</Text> Health and life insurance premiums</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>Income Tax:</Text> TDS deducted based on tax slabs</Text>
        <Text style={styles.bulletPoint}>• <Text style={styles.boldText}>Other Deductions:</Text> Loan EMIs, canteen charges, etc.</Text>
      </View>

      <Text style={styles.infoSubtitle}>How to Use This Calculator</Text>
      <View style={styles.stepsList}>
        <Text style={styles.stepText}>Step 1: Enter your annual Cost to Company (CTC)</Text>
        <Text style={styles.stepText}>Step 2: Specify bonus details (percentage or fixed amount)</Text>
        <Text style={styles.stepText}>Step 3: Enable monthly deductions if you want detailed breakdown</Text>
        <Text style={styles.stepText}>Step 4: Enter specific deduction amounts for accurate calculation</Text>
        <Text style={styles.stepText}>Step 5: View your net take-home salary and detailed breakdown</Text>
      </View>

      <Text style={styles.infoSubtitle}>Benefits of Salary Planning</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• Plan your monthly budget based on actual take-home salary</Text>
        <Text style={styles.bulletPoint}>• Understand tax implications and optimize deductions</Text>
        <Text style={styles.bulletPoint}>• Compare job offers by calculating net salary</Text>
        <Text style={styles.bulletPoint}>• Plan investments and savings effectively</Text>
        <Text style={styles.bulletPoint}>• Negotiate salary components for tax optimization</Text>
      </View>
    </View>
  );

  const renderFAQSection = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity 
        style={styles.faqHeader}
        onPress={() => setShowFAQ(!showFAQ)}
      >
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons 
          name={showFAQ ? "chevron-up" : "chevron-down"} 
          size={24} 
          color={colors.primary} 
        />
      </TouchableOpacity>
      
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: What is the difference between CTC and take-home salary?</Text>
            <Text style={styles.faqAnswer}>
              A: CTC (Cost to Company) is the total amount a company spends on an employee, including salary, benefits, and employer contributions. Take-home salary is the actual amount you receive after all deductions like PF, professional tax, and income tax.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: How is basic salary calculated from CTC?</Text>
            <Text style={styles.faqAnswer}>
              A: Basic salary is typically 40-50% of your CTC. It forms the foundation for calculating other components like HRA (usually 40-50% of basic), PF contributions (12% of basic), and gratuity calculations.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: What is Professional Tax and how much do I need to pay?</Text>
            <Text style={styles.faqAnswer}>
              A: Professional Tax is a state-level tax that varies by state. It ranges from ₹200 to ₹2,500 annually. States like Delhi and Haryana don't have professional tax, while Maharashtra has the highest rates.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: How can I optimize my salary structure for tax savings?</Text>
            <Text style={styles.faqAnswer}>
              A: You can optimize by maximizing tax-free allowances like HRA, transport allowance, meal vouchers, and mobile reimbursements. Also, contribute to ELSS, PPF, and claim deductions under Section 80C, 80D for health insurance.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Q: Is this salary calculator accurate for all companies?</Text>
            <Text style={styles.faqAnswer}>
              A: This calculator provides a good estimate based on standard salary structures. However, actual calculations may vary based on your company's specific policies, state laws, and individual tax situations. Consult your HR for exact figures.
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: '#4F46E5',
      paddingHorizontal: 20,
      paddingVertical: 40,
      paddingTop: isMobile ? 60 : 40,
    },
    headerTitle: {
      fontSize: isMobile ? 24 : 32,
      fontWeight: 'bold',
      color: 'white',
      marginBottom: 12,
      textAlign: isMobile ? 'center' : 'left',
    },
    headerSubtitle: {
      fontSize: 16,
      color: 'rgba(255, 255, 255, 0.9)',
      lineHeight: 24,
      marginBottom: 20,
      textAlign: isMobile ? 'center' : 'left',
    },
    headerTags: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      justifyContent: isMobile ? 'center' : 'flex-start',
    },
    headerTag: {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 16,
    },
    headerTagText: {
      color: 'white',
      fontSize: 12,
      fontWeight: '500',
    },
    hamburgerButton: {
      position: 'absolute',
      top: 20,
      left: 20,
      zIndex: 1,
    },
    calculatorContainer: {
      backgroundColor: colors.background,
      margin: 0,
      padding: 20,
    },
    formContent: {
      gap: 20,
      flex: 0.7,
    },
    calculatorRow: {
      flexDirection: 'row',
      gap: 24,
    },
    summaryPanel: {
      flex: 0.3,
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 20,
      height: 'fit-content',
      borderWidth: 1,
      borderColor: colors.border,
    },
    panelHeader: {
      marginBottom: 20,
    },
    panelTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
    },
    panelSubtitle: {
      fontSize: 14,
      color: colors.textSecondary,
      marginBottom: 8,
    },
    panelAmount: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.success || '#059669',
    },
    taxInfo: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    infoTitle: {
      fontSize: 14,
      color: colors.textSecondary,
      flex: 1,
    },
    infoValue: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      textAlign: 'right',
    },
    regimeInfo: {
      marginTop: 16,
      padding: 16,
      backgroundColor: colors.primary + '10',
      borderRadius: 8,
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
    },
    regimeTitle: {
      fontSize: 13,
      color: colors.primary,
      lineHeight: 18,
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.background,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: 16,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      marginRight: 8,
    },
    percentSymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      marginLeft: 8,
    },
    input: {
      flex: 1,
      paddingVertical: 16,
      fontSize: 16,
      color: colors.text,
      borderWidth: 0,
    },
    dropdownContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: colors.background,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      paddingHorizontal: 16,
      paddingVertical: 16,
      marginBottom: 8,
    },
    dropdownText: {
      fontSize: 16,
      color: colors.text,
    },
    dropdownMenu: {
      backgroundColor: colors.cardBackground,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
      marginBottom: 8,
      elevation: 3,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    dropdownItem: {
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    dropdownItemText: {
      fontSize: 16,
      color: colors.text,
    },
    toggleRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    checkbox: {
      width: 24,
      height: 24,
      borderRadius: 4,
      borderWidth: 2,
      borderColor: colors.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkboxActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    deductionsContainer: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 16,
      marginTop: 8,
    },
    resultsContainer: {
      margin: 20,
      gap: 12,
    },
    resultCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 20,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    primaryCard: {
      backgroundColor: colors.primary + '10',
      borderWidth: 1,
      borderColor: colors.primary + '30',
    },
    resultTitle: {
      fontSize: 16,
      color: colors.textSecondary,
      fontWeight: '500',
    },
    resultAmount: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
    },
    primaryAmount: {
      color: colors.primary,
      fontSize: 20,
    },
    breakdownContainer: {
      backgroundColor: colors.cardBackground,
      margin: 20,
      borderRadius: 12,
      padding: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    breakdownTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
    },
    breakdownTable: {
      borderRadius: 8,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: colors.border,
    },
    tableHeader: {
      flexDirection: 'row',
      backgroundColor: colors.primary,
      paddingVertical: 12,
      paddingHorizontal: 16,
    },
    tableHeaderText: {
      flex: 1,
      fontSize: 14,
      fontWeight: 'bold',
      color: '#ffffff',
      textAlign: 'center',
    },
    tableRow: {
      flexDirection: 'row',
      paddingVertical: 12,
      paddingHorizontal: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      backgroundColor: colors.background,
    },
    tableCell: {
      flex: 1,
      fontSize: 14,
      color: colors.text,
      textAlign: 'center',
    },
    infoContainer: {
      backgroundColor: colors.cardBackground,
      margin: 20,
      padding: 24,
      borderRadius: 12,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 2,
    },
    infoTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 16,
    },
    infoSubtitle: {
      fontSize: 20,
      fontWeight: '600',
      color: colors.text,
      marginTop: 24,
      marginBottom: 12,
    },
    infoText: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 16,
    },
    bulletList: {
      marginTop: 12,
    },
    bulletPoint: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 8,
    },
    boldText: {
      fontWeight: 'bold',
      color: colors.text,
    },
    stepsList: {
      marginTop: 12,
    },
    stepText: {
      fontSize: 16,
      color: colors.textSecondary,
      lineHeight: 24,
      marginBottom: 8,
    },
    faqContainer: {
      backgroundColor: colors.cardBackground,
      margin: 20,
      borderRadius: 12,
      overflow: 'hidden',
      elevation: 2,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: 20,
      backgroundColor: colors.primary + '10',
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      padding: 20,
    },
    faqItem: {
      marginBottom: 20,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 15,
      color: colors.textSecondary,
      lineHeight: 22,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      {renderCalculator()}
      {renderResults()}
      {renderSalaryBreakdown()}
      {renderSalaryInformation()}
      {renderFAQSection()}
      <Footer />
    </ScrollView>
  );
};

export default SalaryCalculatorScreen;